import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowRight, Shield, Truck, FlaskConical, Sparkles } from 'lucide-react';

const HeroSection = () => {
  const { currentLanguage } = useLanguage();

  const content = {
    en: {
      title1: 'Revolutionary',
      highlight: 'Peptide Pens',
      title2: 'for Research Excellence',
      subtitle: 'First-to-market pre-mixed, stabilized peptide delivery system. No reconstitution needed - precision dosing made simple.',
      cta: 'Explore Products',
      feature1: '99%+ Purity',
      feature2: 'Fast EU Delivery',
      feature3: 'Lab Tested'
    },
    es: {
      title1: 'Revolucionarias',
      highlight: 'Plumas de Péptidos',
      title2: 'para Investigación de Excelencia',
      subtitle: 'Sistema de administración de péptidos premezclados y estabilizados, pionero en el mercado. Sin reconstitución necesaria - dosificación precisa simplificada.',
      cta: 'Explorar Productos',
      feature1: '99%+ Pureza',
      feature2: 'Envío Rápido EU',
      feature3: 'Testado en Lab'
    },
    ru: {
      title1: 'Революционные',
      highlight: 'Пептидные Шприц-ручки',
      title2: 'для Передовых Исследований',
      subtitle: 'Первая на рынке готовая к применению стабилизированная система доставки пептидов. Без разведения, точное дозирование стало простым.',
      cta: 'Перейти к каталогу',
      feature1: 'Чистота 99%+',
      feature2: 'Быстрая доставка по ЕС',
      feature3: 'Лабораторные тесты'
    },
    de: {
      title1: 'Revolutionäre',
      highlight: 'Peptid-Pens',
      title2: 'für Forschungsexzellenz',
      subtitle: 'Erstes vorgemischtes, stabilisiertes Peptid-Liefersystem auf dem Markt. Keine Rekonstitution nötig, präzise Dosierung leicht gemacht.',
      cta: 'Produkte entdecken',
      feature1: '99%+ Reinheit',
      feature2: 'Schnelle EU-Lieferung',
      feature3: 'Laborgeprüft'
    },
    fr: {
      title1: 'Révolutionnaires',
      highlight: 'Stylos à Peptides',
      title2: 'pour l\'Excellence en Recherche',
      subtitle: 'Premier système d\'administration de peptides pré-mélangés et stabilisés du marché. Sans reconstitution, dosage précis simplifié.',
      cta: 'Explorer les produits',
      feature1: 'Pureté 99%+',
      feature2: 'Livraison rapide UE',
      feature3: 'Testé en laboratoire'
    },
    it: {
      title1: 'Rivoluzionarie',
      highlight: 'Penne Peptidiche',
      title2: 'per l\'Eccellenza nella Ricerca',
      subtitle: 'Primo sistema di somministrazione di peptidi pre-miscelati e stabilizzati sul mercato. Nessuna ricostituzione necessaria, dosaggio preciso semplificato.',
      cta: 'Esplora i prodotti',
      feature1: 'Purezza 99%+',
      feature2: 'Spedizione UE rapida',
      feature3: 'Testato in laboratorio'
    },
    pl: {
      title1: 'Rewolucyjne',
      highlight: 'Peny peptydowe',
      title2: 'dla doskonałości badawczej',
      subtitle: 'Pierwszy na rynku system dostarczania pre-mieszanych, stabilizowanych peptydów. Bez rekonstytucji, precyzyjne dawkowanie w prostej formie.',
      cta: 'Odkryj produkty',
      feature1: 'Czystość 99%+',
      feature2: 'Szybka dostawa UE',
      feature3: 'Testowane w laboratorium'
    },
    nl: {
      title1: 'Revolutionaire',
      highlight: 'Peptide-pennen',
      title2: 'voor onderzoek van topkwaliteit',
      subtitle: 'Eerste voorgemengde, gestabiliseerde peptide-toedieningssysteem op de markt. Geen reconstitutie nodig, nauwkeurige dosering eenvoudig gemaakt.',
      cta: 'Producten ontdekken',
      feature1: '99%+ zuiverheid',
      feature2: 'Snelle EU-levering',
      feature3: 'Laboratorium getest'
    },
    pt: {
      title1: 'Revolucionárias',
      highlight: 'Canetas de Peptídeos',
      title2: 'para Excelência em Pesquisa',
      subtitle: 'Primeiro sistema de administração de peptídeos pré-misturados e estabilizados do mercado. Sem reconstituição, dosagem precisa simplificada.',
      cta: 'Explorar produtos',
      feature1: 'Pureza 99%+',
      feature2: 'Entrega rápida UE',
      feature3: 'Testado em laboratório'
    }
  };

  const t = content[currentLanguage] || content.en;

  return (
    <section className="relative min-h-[550px] md:min-h-[650px] overflow-hidden bg-capsule-bg water-drops">
      {/* Background Elements */}
      <div className="absolute inset-0">
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-capsule-bg via-capsule-bg-light to-capsule-bg"></div>
        
        {/* Floating spheres - Rose gold */}
        <div className="absolute top-20 right-20 w-32 h-32 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-2xl floating-sphere"></div>
        <div className="absolute bottom-32 right-40 w-24 h-24 rounded-full bg-gradient-to-br from-capsule-rose-gold/25 to-capsule-pink/20 blur-xl floating-sphere" style={{ animationDelay: '-2s' }}></div>
        <div className="absolute top-40 left-20 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-teal/20 to-capsule-aqua/20 blur-xl floating-sphere" style={{ animationDelay: '-4s' }}></div>
        
        {/* Chrome spheres */}
        <div className="absolute top-1/3 right-1/4 w-6 h-6 rounded-full bg-gradient-to-br from-capsule-silver to-capsule-chrome shadow-lg floating-sphere"></div>
        <div className="absolute bottom-1/4 right-1/3 w-4 h-4 rounded-full bg-gradient-to-br from-capsule-rose-gold to-capsule-salmon shadow-md floating-sphere" style={{ animationDelay: '-3s' }}></div>
        
        {/* DNA Helix Decoration - Right Side */}
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-96 h-[500px] opacity-30 hidden lg:block">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            {/* DNA Helix Strands */}
            <defs>
              <linearGradient id="dnaGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="50%" stopColor="#22D3EE" />
                <stop offset="100%" stopColor="#14B8A6" />
              </linearGradient>
              <linearGradient id="dnaGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#F97068" />
                <stop offset="50%" stopColor="#FDA4AF" />
                <stop offset="100%" stopColor="#F97068" />
              </linearGradient>
            </defs>
            {/* Left strand */}
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#dnaGradient)" strokeWidth="4" fill="none" opacity="0.8"/>
            {/* Right strand */}
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#dnaGradient)" strokeWidth="4" fill="none" opacity="0.8"/>
            {/* Connecting rungs */}
            <line x1="50" y1="50" x2="150" y2="50" stroke="url(#dnaGradient2)" strokeWidth="3" opacity="0.6"/>
            <line x1="50" y1="100" x2="150" y2="100" stroke="url(#dnaGradient)" strokeWidth="3" opacity="0.6"/>
            <line x1="50" y1="150" x2="150" y2="150" stroke="url(#dnaGradient2)" strokeWidth="3" opacity="0.6"/>
            <line x1="50" y1="200" x2="150" y2="200" stroke="url(#dnaGradient)" strokeWidth="3" opacity="0.6"/>
            <line x1="50" y1="250" x2="150" y2="250" stroke="url(#dnaGradient2)" strokeWidth="3" opacity="0.6"/>
            <line x1="50" y1="300" x2="150" y2="300" stroke="url(#dnaGradient)" strokeWidth="3" opacity="0.6"/>
            <line x1="50" y1="350" x2="150" y2="350" stroke="url(#dnaGradient2)" strokeWidth="3" opacity="0.6"/>
          </svg>
        </div>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-md border border-capsule-teal/30 rounded-full px-4 py-2 mb-6 shadow-soft">
            <Sparkles className="w-4 h-4 text-capsule-teal" />
            <span className="text-capsule-teal-dark text-sm font-semibold">Premium Research Peptides</span>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
            {t.title1}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-capsule-teal via-capsule-aqua to-capsule-teal">
              {t.highlight}
            </span>
            <br />
            <span className="text-gray-700">{t.title2}</span>
          </h1>
          
          {/* Subtitle */}
          <p className="text-gray-600 text-base sm:text-lg md:text-xl leading-relaxed mb-8 max-w-xl">
            {t.subtitle}
          </p>
          
          {/* CTA Button */}
          <Link 
            to="/products"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg transition-all duration-300 shadow-teal-glow hover:shadow-lg hover:scale-105"
            data-testid="hero-cta-btn"
          >
            {t.cta}
            <ArrowRight className="w-5 h-5" />
          </Link>

          {/* Feature Pills */}
          <div className="flex flex-wrap gap-3 sm:gap-4 mt-10 sm:mt-12">
            <div className="glass-card flex items-center gap-2 rounded-full px-4 py-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                <Shield className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-700 text-sm font-medium">{t.feature1}</span>
            </div>
            <div className="glass-card flex items-center gap-2 rounded-full px-4 py-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-capsule-teal to-capsule-aqua flex items-center justify-center">
                <Truck className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-700 text-sm font-medium">{t.feature2}</span>
            </div>
            <div className="glass-card flex items-center gap-2 rounded-full px-4 py-2.5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-capsule-coral to-capsule-salmon flex items-center justify-center">
                <FlaskConical className="w-4 h-4 text-white" />
              </div>
              <span className="text-gray-700 text-sm font-medium">{t.feature3}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path 
            d="M0 80L60 70C120 60 240 40 360 33.3C480 27 600 33 720 40C840 47 960 53 1080 50C1200 47 1320 33 1380 26.7L1440 20V80H1380C1320 80 1200 80 1080 80C960 80 840 80 720 80C600 80 480 80 360 80C240 80 120 80 60 80H0Z" 
            fill="white"
          />
        </svg>
      </div>
    </section>
  );
};

export default HeroSection;
